/**
 * Convert a civil wall time in an IANA zone to a UTC instant.
 * Used for Eastern lottery draw clocks (DST-aware). Cuba house time stays fixed UTC−5.
 */

export interface ZonedTimeParts {
  year: number;
  month: number;
  day: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export const EASTERN_TIMEZONE = 'America/New_York';

export function getZonedTimeParts(timeZone: string, utcInstant: Date): ZonedTimeParts {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(utcInstant);
  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  let hours = pick('hour');
  // Some engines emit "24" for midnight.
  if (hours === 24) hours = 0;
  return {
    year: pick('year'),
    month: pick('month'),
    day: pick('day'),
    hours,
    minutes: pick('minute'),
    seconds: pick('second'),
  };
}

/** Wall clock in `timeZone` → UTC instant (iterative; handles DST). */
export function zonedWallTimeToUtc(
  timeZone: string,
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second = 0,
): Date {
  let utcMs = Date.UTC(year, month - 1, day, hour + 5, minute, second);
  for (let i = 0; i < 4; i += 1) {
    const got = getZonedTimeParts(timeZone, new Date(utcMs));
    const wantMs = Date.UTC(year, month - 1, day, hour, minute, second);
    const gotMs = Date.UTC(
      got.year, got.month - 1, got.day, got.hours, got.minutes, got.seconds,
    );
    const delta = wantMs - gotMs;
    if (delta === 0) break;
    utcMs += delta;
  }
  return new Date(utcMs);
}

export function parseHHMM(value: string): { hour: number; minute: number } {
  const [hour, minute] = value.split(':').map(Number);
  return { hour, minute };
}

export function formatHHMM(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}
